import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { KeycloakService } from 'keycloak-angular';
import { UserInteractionApiService } from '@angular-workspace/services';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { AdminSidebarComponent } from '../sidebar/admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { FilterField, FilterPanelComponent } from '../../shared/components/filter-panel/filter-panel.component';
import {
  ModalComponent,
  PaginationConfig,
  TableColumn,
  TableComponent,
  withCellTemplates,
} from '../../shared/components/primeng';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { DateRange } from '../../shared/components/primeng/date-range-picker/date-range-picker.component';
import { ActionDropdownOverlayController } from '../../shared/utils/action-dropdown-overlay.sync';
import { feedbackListTotal, feedbackRejectionComment } from '../../shared/utils/user-feedback.util';
import { ToasterService } from '../../services/toaster.service';
import { FormatService } from '../../shared/services/format.service';

export type FeedbackStatus = 'pending' | 'approved' | 'rejected';

export interface ManageFeedbackRow {
  id: string;
  assetName: string;
  assetType: string;
  assetId: string;
  consumerName: string;
  organisation: string;
  userFeedback: string;
  rating: string;
  status: FeedbackStatus;
  rejectionComment: string;
  createdAt: string;
  createdAtRaw: string;
  originalData: Record<string, unknown>;
}

@Component({
  selector: 'app-admin-manage-feedback',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    AdminSidebarComponent,
    PageHeaderComponent,
    FilterPanelComponent,
    TableComponent,
    ModalComponent,
    ConfirmationDialogComponent,
  ],
  templateUrl: './manage-feedback.component.html',
  styleUrl: './manage-feedback.component.scss',
})
export class AdminManageFeedbackComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<unknown>;
  @ViewChild('feedbackCell', { static: false }) feedbackCellTemplate!: TemplateRef<unknown>;
  @ViewChild('ratingCell', { static: false }) ratingCellTemplate!: TemplateRef<unknown>;
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<unknown>;

  private readonly userInteractionApi = inject(UserInteractionApiService);
  private readonly toaster = inject(ToasterService);
  private readonly formatService = inject(FormatService);
  private readonly keycloak = inject(KeycloakService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly actionDropdownOverlay = new ActionDropdownOverlayController();

  filterFields: FilterField[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'multiselect',
      placeholder: 'Choose Status',
      options: [
        { label: 'All', value: 'all' },
        { label: 'Pending', value: 'pending' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' },
      ],
      value: [{ label: 'All', value: 'all' }],
      maxSelected: 1,
      showClear: false,
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      type: 'dateRange',
      placeholder: 'Choose date range',
      value: null,
    },
  ];

  columns: TableColumn[] = [
    { key: 'assetName', label: 'Asset', sortable: false, width: '150px' },
    { key: 'assetType', label: 'Asset Type', sortable: false, width: '130px' },
    { key: 'consumerName', label: 'Consumer Name', sortable: false, width: '170px' },
    { key: 'organisation', label: 'Organisation', sortable: false, width: '170px' },
    { key: 'userFeedback', label: 'User Feedback', sortable: false, width: '200px' },
    { key: 'rating', label: 'Rating', sortable: false, width: '120px' },
    { key: 'status', label: 'Status', sortable: false, width: '120px' },
    { key: 'createdAt', label: 'Created At', sortable: false, width: '160px' },
    { key: 'action', label: 'Action', sortable: false, fixed: 'right', width: '110px' },
  ];

  rows: ManageFeedbackRow[] = [];
  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true,
  };
  loading = false;
  emptyMessage = 'No feedback found';

  private dateRange: DateRange | null = null;
  private statusFilter: 'all' | FeedbackStatus = 'all';
  private isInitialFilterLoad = true;

  openDropdownRowIndex: number | null = null;
  dropdownPosition: { top: number; left: number | null; right: number | null } | null = null;
  isApproveConfirmationVisible = false;
  isRejectConfirmationVisible = false;
  showFeedbackModal = false;
  feedbackModalRow: ManageFeedbackRow | null = null;
  rejectReason = '';
  showRejectReasonError = false;
  statusActionLoading = false;
  readonly rejectReasonMax = 200;
  private selectedRow: ManageFeedbackRow | null = null;

  async ngOnInit(): Promise<void> {
    await this.waitForKeycloakReady();
    await this.loadFeedback();
    setTimeout(() => {
      this.isInitialFilterLoad = false;
    }, 200);
  }

  ngAfterViewInit(): void {
    const updates: { key: string; cellTemplate: TemplateRef<unknown> }[] = [];
    if (this.feedbackCellTemplate) {
      updates.push({ key: 'userFeedback', cellTemplate: this.feedbackCellTemplate });
    }
    if (this.ratingCellTemplate) {
      updates.push({ key: 'rating', cellTemplate: this.ratingCellTemplate });
    }
    if (this.statusCellTemplate) {
      updates.push({ key: 'status', cellTemplate: this.statusCellTemplate });
    }
    if (this.actionCellTemplate) {
      updates.push({ key: 'action', cellTemplate: this.actionCellTemplate });
    }
    if (updates.length) {
      this.columns = withCellTemplates(this.columns, updates);
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.closeActionDropdown();
  }

  onFilterChange(filters: Record<string, unknown>): void {
    if (this.isInitialFilterLoad) {
      return;
    }
    if (filters['dateRange'] !== undefined) {
      this.dateRange = (filters['dateRange'] as DateRange | null) ?? null;
    }
    if (filters['status'] !== undefined) {
      this.statusFilter = this.resolveStatusFilter(filters['status']);
    }
    this.pagination = { ...this.pagination, currentPage: 1 };
    void this.loadFeedback();
  }

  onClearFilters(): void {
    this.dateRange = null;
    this.statusFilter = 'all';
    this.pagination = { ...this.pagination, currentPage: 1 };
    void this.loadFeedback();
  }

  onPageChange(page: number): void {
    this.pagination = { ...this.pagination, currentPage: page };
    void this.loadFeedback();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = { ...this.pagination, pageSize, currentPage: 1 };
    void this.loadFeedback();
  }

  get openDropdownRow(): ManageFeedbackRow | null {
    if (this.openDropdownRowIndex == null) {
      return null;
    }
    return this.rows[this.openDropdownRowIndex] ?? null;
  }

  isDropdownOpen(rowIndex: number): boolean {
    return this.openDropdownRowIndex === rowIndex;
  }

  canApproveOrReject(row: ManageFeedbackRow): boolean {
    return row.status === 'pending';
  }

  hasFeedbackText(row: ManageFeedbackRow): boolean {
    const text = (row.userFeedback || '').trim();
    return text.length > 0 && text !== '—';
  }

  ratingValue(row: ManageFeedbackRow | null | undefined): number {
    const numeric = Number(row?.rating);
    return Number.isFinite(numeric) ? Math.min(5, Math.max(0, Math.round(numeric))) : 0;
  }

  isStarFilled(row: ManageFeedbackRow, star: number): boolean {
    return star <= this.ratingValue(row);
  }

  get feedbackModalRating(): number {
    return this.ratingValue(this.feedbackModalRow);
  }

  feedbackAuthorName(row: ManageFeedbackRow): string {
    return this.collapseRepeatedName(row.consumerName) || 'Unknown';
  }

  feedbackAuthorOrg(row: ManageFeedbackRow): string {
    const org = (row.organisation || '').trim();
    if (!org || org === 'N/A' || org === this.feedbackAuthorName(row)) {
      return '';
    }
    return org;
  }

  feedbackAuthorInitials(row: ManageFeedbackRow): string {
    const source = this.feedbackAuthorName(row);
    const parts = source.split(/[\s@._-]+/).filter((part) => part.length > 0);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return (parts[0] ?? 'U').substring(0, 2).toUpperCase();
  }

  openFeedbackModal(row: ManageFeedbackRow, event?: Event): void {
    event?.stopPropagation();
    this.closeActionDropdown();
    this.feedbackModalRow = row;
    this.showFeedbackModal = true;
    this.cdr.markForCheck();
  }

  closeFeedbackModal(): void {
    this.showFeedbackModal = false;
    this.feedbackModalRow = null;
  }

  onFeedbackModalVisibleChange(visible: boolean): void {
    this.showFeedbackModal = visible;
    if (!visible) {
      this.feedbackModalRow = null;
    }
  }

  statusLabel(status: FeedbackStatus): string {
    if (status === 'approved') {
      return 'Approved';
    }
    if (status === 'rejected') {
      return 'Rejected';
    }
    return 'Pending';
  }

  statusChipClass(status: FeedbackStatus): string {
    if (status === 'approved') {
      return 'status-chip status-chip-granted';
    }
    if (status === 'rejected') {
      return 'status-chip status-chip-rejected';
    }
    return 'status-chip status-chip-pending';
  }

  toggleActionDropdown(event: Event, rowIndex: number): void {
    event.stopPropagation();
    if (this.openDropdownRowIndex === rowIndex) {
      this.closeActionDropdown();
      return;
    }
    const button = event.currentTarget as HTMLElement;
    this.openDropdownRowIndex = rowIndex;
    this.actionDropdownOverlay.open({
      anchor: button,
      itemCount: 3,
      onPosition: (pos) => {
        this.dropdownPosition = { top: pos.top, left: pos.left, right: pos.right };
        this.cdr.markForCheck();
      },
      onAnchorDetached: () => this.closeActionDropdown(),
    });
    this.cdr.detectChanges();
  }

  closeActionDropdown(): void {
    this.actionDropdownOverlay.close();
    this.openDropdownRowIndex = null;
    this.dropdownPosition = null;
    this.cdr.detectChanges();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (
      this.openDropdownRowIndex !== null &&
      !target.closest('.action-cell-wrapper') &&
      !target.closest('.action-dropdown-overlay')
    ) {
      this.closeActionDropdown();
    }
  }

  handleAction(action: 'view' | 'approve' | 'reject', row: ManageFeedbackRow): void {
    this.closeActionDropdown();
    if (action === 'view') {
      this.openFeedbackModal(row);
      return;
    }
    if (!this.canApproveOrReject(row)) {
      return;
    }
    this.selectedRow = row;
    if (action === 'approve') {
      this.isApproveConfirmationVisible = true;
      return;
    }
    this.rejectReason = '';
    this.showRejectReasonError = false;
    this.isRejectConfirmationVisible = true;
  }

  getApproveMessage(): string {
    const name = this.selectedRow?.assetName || 'this asset';
    return `Are you sure you want to approve this feedback for <strong>${name}</strong>? It will become visible on the catalogue.`;
  }

  getRejectMessage(): string {
    const name = this.selectedRow?.assetName || 'this asset';
    return `Are you sure you want to reject this feedback for <strong>${name}</strong>? It will not be shown on the catalogue.`;
  }

  get rejectReasonLength(): number {
    return this.rejectReason.length;
  }

  get canSubmitReject(): boolean {
    return this.rejectReason.trim().length > 0 && this.rejectReason.trim().length <= this.rejectReasonMax;
  }

  onRejectReasonChange(): void {
    if (this.rejectReason.trim()) {
      this.showRejectReasonError = false;
    }
  }

  async onApproveConfirm(): Promise<void> {
    await this.submitStatus('APPROVED');
  }

  async onRejectConfirm(): Promise<void> {
    if (!this.rejectReason.trim()) {
      this.showRejectReasonError = true;
      this.cdr.markForCheck();
      return;
    }
    await this.submitStatus('REJECTED');
  }

  onApproveCancel(): void {
    if (this.statusActionLoading) {
      return;
    }
    this.isApproveConfirmationVisible = false;
    this.selectedRow = null;
  }

  onRejectCancel(): void {
    if (this.statusActionLoading) {
      return;
    }
    this.isRejectConfirmationVisible = false;
    this.rejectReason = '';
    this.showRejectReasonError = false;
    this.selectedRow = null;
  }

  onRejectModalVisibleChange(visible: boolean): void {
    this.isRejectConfirmationVisible = visible;
    if (!visible && !this.statusActionLoading) {
      this.rejectReason = '';
      this.showRejectReasonError = false;
      this.selectedRow = null;
    }
  }

  private async loadFeedback(): Promise<void> {
    this.loading = true;
    this.closeActionDropdown();
    this.closeFeedbackModal();
    try {
      const queryParams: Record<string, unknown> = {
        page: this.pagination.currentPage,
        size: this.pagination.pageSize,
      };
      const range = this.toApiDateRange(this.dateRange);
      if (range) {
        queryParams['time'] = range.time;
        queryParams['endtime'] = range.endtime;
        queryParams['timerel'] = 'between';
      }
      if (this.statusFilter !== 'all') {
        queryParams['feedbackStatus'] = this.statusFilter.toUpperCase();
      }

      const response = await this.userInteractionApi.getPlatformFeedback(queryParams);
      const items = this.extractList(response);
      let mapped = items.map((item) => this.mapRow(item));
      mapped = mapped.filter((row) => this.matchesDateRange(row) && this.matchesStatus(row));

      // ponytail: a payload bigger than pageSize is paged locally; a larger server total on that payload is ignored.
      const oversized = items.length > this.pagination.pageSize;
      this.rows = mapped;
      this.pagination = {
        ...this.pagination,
        serverSide: !oversized,
        totalItems: oversized ? mapped.length : feedbackListTotal(response, items.length),
      };
    } catch (error) {
      console.error('Error loading feedback:', error);
      this.rows = [];
      this.pagination = { ...this.pagination, totalItems: 0 };
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  private async submitStatus(status: 'APPROVED' | 'REJECTED'): Promise<void> {
    const row = this.selectedRow;
    if (!row?.id) {
      this.selectedRow = null;
      this.isApproveConfirmationVisible = false;
      this.isRejectConfirmationVisible = false;
      this.toaster.error('Feedback id is missing.');
      return;
    }

    const comment = status === 'REJECTED' ? this.rejectReason.trim() : undefined;
    if (status === 'REJECTED' && !comment) {
      this.showRejectReasonError = true;
      this.cdr.markForCheck();
      return;
    }

    this.statusActionLoading = true;
    this.cdr.markForCheck();
    try {
      await this.userInteractionApi.updatePlatformFeedbackStatus({
        id: row.id,
        status,
        comment,
      });
      this.toaster.success(
        status === 'APPROVED' ? 'Feedback approved.' : 'Feedback rejected.',
      );
      this.isApproveConfirmationVisible = false;
      this.isRejectConfirmationVisible = false;
      this.rejectReason = '';
      this.showRejectReasonError = false;
      this.selectedRow = null;
      await this.loadFeedback();
    } catch (error) {
      console.error('Error updating feedback status:', error);
    } finally {
      this.statusActionLoading = false;
      this.cdr.markForCheck();
    }
  }

  private mapRow(item: unknown): ManageFeedbackRow {
    const source = this.asRecord(item);
    const user = this.asRecord(source['user'] ?? source['consumer'] ?? source['createdBy']);
    const asset = this.asRecord(source['asset'] ?? source['item']);
    const userFeedback = this.readUserFeedback(source);
    const status = this.readStatus(source);
    const createdAtRaw = this.readString(source, [
      'ratingCreatedAt',
      'ratingUpdatedAt',
      'createdAt',
      'created_at',
      'updatedAt',
      'updated_at',
      'submittedAt',
    ]);
    const assetId = this.readString(source, ['assetId', 'asset_id', 'itemId']) ||
      this.readString(asset, ['id', 'assetId']);
    const assetType = this.readString(source, ['assetType', 'asset_type']) ||
      this.readString(asset, ['type', 'assetType']);
    const namedAsset =
      this.readString(source, ['assetName', 'asset_name', 'itemName', 'label', 'name']) ||
      this.readString(asset, ['label', 'name', 'assetName']);
    const assetName =
      namedAsset ||
      (assetId && assetType ? `${assetType} (${assetId})` : '') ||
      assetId ||
      'N/A';

    return {
      id: this.readString(source, ['id', 'feedbackId', '_id']) || '',
      assetName,
      assetType: this.assetTypeLabel(assetType),
      assetId: assetId || 'N/A',
      consumerName: this.readConsumerName(source, user),
      organisation: this.readOrganisation(source, user),
      userFeedback: userFeedback || '—',
      rating: this.readRating(source),
      status,
      rejectionComment: feedbackRejectionComment(source, userFeedback, status),
      createdAt: createdAtRaw ? this.formatService.formatDate(createdAtRaw) : '—',
      createdAtRaw,
      originalData: source,
    };
  }

  private assetTypeLabel(raw: string): string {
    const type = raw.trim().toUpperCase().replace(/[\s_-]/g, '');
    if (type.includes('DATABANK') || type === 'DATASET') return 'Dataset';
    if (type.includes('AIMODEL') || type === 'MODEL') return 'Model';
    if (type.includes('USECASE') || type === 'APP') return 'Use Case';
    return raw.trim() || 'N/A';
  }

  private readConsumerName(
    source: Record<string, unknown>,
    user: Record<string, unknown>,
  ): string {
    const name =
      this.readString(user, ['fullName', 'name', 'userName']) ||
      this.readString(source, ['consumerName', 'userName', 'user_name', 'fullName']) ||
      this.readString(source, ['userEmail', 'email']) ||
      this.readString(user, ['email', 'userEmail']);
    return this.collapseRepeatedName(name) || 'N/A';
  }

  private readOrganisation(
    source: Record<string, unknown>,
    user: Record<string, unknown>,
  ): string {
    return (
      this.readString(source, ['orgName', 'organisationName', 'organisation', 'organization']) ||
      this.readString(user, ['orgName', 'organisationName', 'organisation', 'organization']) ||
      'N/A'
    );
  }

  private collapseRepeatedName(value: string): string {
    const text = value.trim();
    const parts = text.split(/\s+/).filter(Boolean);
    if (parts.length === 2 && parts[0].toLowerCase() === parts[1].toLowerCase()) {
      return parts[0];
    }
    return text;
  }

  private readStatus(source: Record<string, unknown>): FeedbackStatus {
    const raw = this.readString(source, [
      'feedbackStatus',
      'status',
      'approvalStatus',
      'requestStatus',
    ]).toLowerCase();
    if (raw === 'approved' || raw === 'granted') {
      return 'approved';
    }
    if (raw === 'rejected' || raw === 'denied') {
      return 'rejected';
    }
    return 'pending';
  }

  private readRating(source: Record<string, unknown>): string {
    const value = source['entityRating'] ?? source['rating'] ?? source['stars'];
    if (value == null || value === '') {
      return '—';
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? String(numeric) : String(value);
  }

  private readUserFeedback(source: Record<string, unknown>): string {
    return (
      this.readCommentFromSubdata(source['actionSubdata']) ||
      this.readString(source, ['feedback', 'userFeedback', 'review', 'comment'])
    );
  }

  private readCommentFromSubdata(value: unknown): string {
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value) as unknown;
        return this.readCommentFromSubdata(parsed);
      } catch {
        return value.trim();
      }
    }
    const record = this.asRecord(value);
    return this.readString(record, ['comment', 'feedback', 'text', 'message']);
  }

  private matchesDateRange(row: ManageFeedbackRow): boolean {
    const start = this.startOfDay(this.dateRange?.startDate ?? null);
    const end = this.endOfDay(this.dateRange?.endDate ?? null);
    if (!start && !end) {
      return true;
    }
    if (!row.createdAtRaw) {
      return false;
    }
    const created = new Date(row.createdAtRaw).getTime();
    if (Number.isNaN(created)) {
      return false;
    }
    if (start && created < start) {
      return false;
    }
    if (end && created > end) {
      return false;
    }
    return true;
  }

  private matchesStatus(row: ManageFeedbackRow): boolean {
    return this.statusFilter === 'all' || row.status === this.statusFilter;
  }

  private resolveStatusFilter(status: unknown): 'all' | FeedbackStatus {
    const value = Array.isArray(status) ? status[0] : status;
    const raw =
      value && typeof value === 'object' && 'value' in value
        ? String((value as { value?: unknown }).value ?? '')
        : String(value ?? '');
    if (raw === 'pending' || raw === 'approved' || raw === 'rejected') {
      return raw;
    }
    return 'all';
  }

  private toApiDateRange(range: DateRange | null): { time: string; endtime: string } | null {
    if (!range?.startDate || !range?.endDate) {
      return null;
    }
    const start = new Date(range.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(range.endDate);
    end.setHours(23, 59, 59, 0);
    return {
      time: this.toLocalDateTime(start),
      endtime: this.toLocalDateTime(end),
    };
  }

  private toLocalDateTime(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  private startOfDay(date: Date | null): number | null {
    if (!date) {
      return null;
    }
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next.getTime();
  }

  private endOfDay(date: Date | null): number | null {
    if (!date) {
      return null;
    }
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next.getTime();
  }

  private extractList(response: unknown): unknown[] {
    const record = this.asRecord(response);
    if (Array.isArray(record['result'])) {
      return record['result'];
    }
    const nestedResult = this.asRecord(record['result']);
    if (Array.isArray(nestedResult['data'])) {
      return nestedResult['data'];
    }
    if (Array.isArray(record['results'])) {
      return record['results'];
    }
    if (Array.isArray(record['data'])) {
      return record['data'];
    }
    if (Array.isArray(response)) {
      return response;
    }
    return [];
  }

  private readString(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }

  private async waitForKeycloakReady(): Promise<void> {
    try {
      const instance = this.keycloak.getKeycloakInstance();
      if (instance?.authenticated && instance?.token) {
        return;
      }
      const maxWaitTime = 5000;
      const checkInterval = 100;
      const startTime = Date.now();
      while (Date.now() - startTime < maxWaitTime) {
        const next = this.keycloak.getKeycloakInstance();
        if (next?.authenticated && next?.token) {
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
      }
    } catch (error) {
      console.error('Error waiting for Keycloak:', error);
    }
  }
}
